import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Box, Button, Grid, Paper, Typography } from '@material-ui/core/';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import StepWizard from 'react-step-wizard';

import HomeAnimations from 'components/animations/HomeAnimations';
import ColumnMiniChart from 'components/charts/ColumnMiniChart';
import LineMiniChart from 'components/charts/LineMiniChart';
import SenslaChart from 'components/SenslaChart';
import { ctx, IStore } from 'store';
import { colors } from 'styles';

import { useStyles } from './styles';

const circleColors = [
  '#e34a4c',
  '#f7773f',
  '#fc8d3a',
  '#ffa438',
  '#e1b631',
  '#c0c53f',
  '#9cd05a',
  '#49cf8c',
  '#00b9d8',
  '#4aa8dc',
];

const UserSurveys: React.FC = () => {
  const classes = useStyles();
  const [questions, setQuestions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<number[]>([]);

  const {
    teams: { currentTeam, currentMemberId },
    surveys: { currentSurvey, surveys },
  } = useContext(ctx.store);
  const actions = useContext(ctx.actions);
  // const classes = useStyles();

  useEffect(() => {
    const fetch = async () => {
      const team = currentTeam.teamId ? currentTeam.teamId : currentTeam.tenantId;
      const platform = currentTeam.teamId ? 'slack' : 'microsoft';
      await ((actions.misc.setLoading(true) as unknown) as Promise<IStore>);
      await ((actions.surveys.getSurveys(team, platform) as unknown) as Promise<IStore>);
      await ((actions.misc.setLoading(false) as unknown) as Promise<IStore>);
    };

    fetch();
  }, []);

  useEffect(() => {
    if (currentSurvey) {
      const surveyQuestions = currentSurvey.questions.length
        ? currentSurvey.questions.map((q: any) => q.question)
        : [];
      setQuestions(surveyQuestions);
      const answerArray = new Array(surveyQuestions.length).fill(0);
      setAnswers(answerArray);
    }
  }, [surveys]);

  const submit = async () => {
    const survey = currentSurvey.id;
    const member = currentMemberId;
    const team = currentTeam.teamId ? currentTeam.teamId : currentTeam.tenantId;
    const platform = currentTeam.teamId ? 'slack' : 'microsoft';
    await ((actions.misc.setLoading(true) as unknown) as Promise<IStore>);
    const answersSent = await ((actions.surveys.sendAnswers(
      team,
      platform,
      member as string,
      answers,
      survey
    ) as unknown) as Promise<IStore>);
    await ((actions.surveys.getSurveys(team, platform) as unknown) as Promise<IStore>);
    if (!answersSent.error) {
      setQuestions([]);
      setAnswers([]);
    }
    await ((actions.misc.setLoading(false) as unknown) as Promise<IStore>);
  };

  const avgSum = () => {
    let sum = 0;
    let count = 0;

    for (const survey of surveys) {
      if (survey.average && survey.hasResponse) {
        sum = sum + survey.average;
        count = count + 1;
      }
    }
    return { sum, count };
  };

  return (
    <div style={{ height: '100vh' }}>
      <Box display="flex" p={1} alignItems="center" className={classes.header}>
        <Box p={1} flexGrow={1}>
          <Typography component="h1" variant="h5">
            Your Surveys Panel
          </Typography>
        </Box>
      </Box>
      <Grid container spacing={2}>
        <Alert severity="warning">
          These questions are related to the team's performance over the past few weeks. It is
          important for you to know that the answers are 100% anonymous and the results are
          presented in aggregate form. These results will provide your company with information that
          will help improve the climate, performance and your workspace.
        </Alert>
        <Grid sm={12} item className={classes.questionsGrid}>
          <Paper className={classes.questionPaper}>
            {currentSurvey && questions.length ? (
              <StepWizard>
                {[
                  ...questions,
                  'Thank you for all your answers, press the final button to send them and close the process.',
                ].map((question: any, index: number) => (
                  <Question
                    key={index}
                    index={index}
                    question={question}
                    setAnswers={(items: any) => setAnswers(items)}
                    answers={answers}
                    submit={submit}
                  />
                ))}
              </StepWizard>
            ) : (
              <Grid
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Grid item xs={3} className={classes.animationGrid}>
                  <HomeAnimations speed={0.6} type="resources" />
                </Grid>
                <Grid item xs={6} className={classes.animationTextGrid}>
                  <Typography className={classes.animationTitle}>
                    You have no surveys to answer, congratulations! Now take your time, go on with
                    your work or read a good recommended article! There is always something good to
                    do!
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Paper>
        </Grid>
        <Grid sm={6} item className={classes.questionsGrid}>
          <Paper className={classes.metricsPaper}>
            <Grid
              xs={12}
              style={{
                alignItems: 'center',
                borderBottom: `2px solid ${colors.greyBg}`,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '10px',
                padding: '5px 0',
                width: '100%',
              }}
            >
              <Typography component="h2" className={classes.subtitle}>
                {`RESPONDED SURVEYS`}
              </Typography>
            </Grid>
            <Grid className={classes.metricsGrid}>
              <Grid>
                <SenslaChart
                  data={
                    surveys.length
                      ? [
                          (surveys.filter((survey: any) => survey.hasResponse).length === 0
                            ? 0
                            : surveys.filter((survey: any) => survey.hasResponse).length) /
                            surveys.length,
                        ]
                      : [0]
                  }
                  name="Responded"
                  color={colors.orangeBrand}
                />
              </Grid>
              <Grid xs={6}>
                <ColumnMiniChart
                  data={
                    surveys.length
                      ? surveys.map((item: any) => {
                          return {
                            x: moment(item.createdAt),
                            y: 1,
                          };
                        })
                      : []
                  }
                  compareData={
                    surveys.length
                      ? surveys.map((item: any) => {
                          return {
                            x: moment(item.createdAt),
                            y: item.hasResponse ? 1 : 0,
                          };
                        })
                      : []
                  }
                  seriesName={['Survey', 'Responded Survey']}
                  color={colors.orangeBrand}
                  isNumber={true}
                  height={200}
                  width={'100%'}
                  strokeWidth={1}
                  showLabelOff={true}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
        <Grid sm={6} item className={classes.questionsGrid}>
          <Paper className={classes.metricsPaper}>
            <Grid
              xs={12}
              style={{
                alignItems: 'center',
                borderBottom: `2px solid ${colors.greyBg}`,
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                marginBottom: '10px',
                padding: '5px 0',
                width: '100%',
              }}
            >
              <Typography component="h2" className={classes.subtitle}>
                {`AVERAGE RESPONSE SCORE`}
              </Typography>
            </Grid>
            <Grid className={classes.metricsGrid}>
              <Grid xs={6}>
                <SenslaChart
                  data={surveys.length ? [avgSum().sum / avgSum().count / 10] : [0]}
                  name="Average Score"
                  color={colors.orangeBrand}
                  isNumber={true}
                />
              </Grid>
              <Grid xs={6}>
                <LineMiniChart
                  data={
                    surveys.length
                      ? surveys.map((item: any) => {
                          return {
                            x: moment(item.createdAt),
                            y: item.average ? item.average.toFixed(1) : 0,
                          };
                        })
                      : []
                  }
                  seriesName={['Average Score']}
                  color={colors.orangeBrand}
                  isNumber={true}
                  height={200}
                  width={'100%'}
                  strokeWidth={3}
                  showLabelOff={false}
                  sizeMarker={surveys.length === 1 ? 2 : 0}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

const Question = (props: any) => {
  const classes = useStyles();
  const chooseQuestion = (option: number) => {
    const newAnswers = props.answers;
    newAnswers[props.index] = option;
    props.setAnswers(newAnswers);
    props.nextStep();
  };

  return (
    <Grid className={classes.questionGrid} item xs={12}>
      <Typography className={classes.questionTitle}>{props.question}</Typography>
      <Grid className={classes.optionsGrid} xs={12}>
        {props.currentStep !== props.totalSteps &&
          [1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((option: any, index: number) => (
            <Button
              variant="outlined"
              className={classes.optionCircle}
              key={index}
              onClick={() => chooseQuestion(option)}
              style={{ border: `6px solid ${circleColors[index]}` }}
            >
              <Typography className={classes.questionTitle}>{option}</Typography>
            </Button>
          ))}
      </Grid>
      <Grid className={classes.optionsGrid}>
        <Typography className={classes.stepText} onClick={props.previousStep}>
          <ArrowBackIcon /> Go Back
        </Typography>
        {props.currentStep === props.totalSteps && (
          <Button
            color="primary"
            variant="contained"
            className={classes.button}
            onClick={props.submit}
          >
            <Typography className={classes.stepText}>Submit</Typography>
          </Button>
        )}
        <Typography
          className={classes.stepText}
        >{`${props.currentStep} / ${props.totalSteps}`}</Typography>
      </Grid>
    </Grid>
  );
};

export { UserSurveys };
